/**
 * Dropbox Zustand Store
 *
 * Centralized state management for Dropbox connection, folder selection,
 * and file listings with AsyncStorage persistence.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { getStorage } from '@/lib/state/ssr-storage';
import type {
  DropboxFile,
  DropboxFolder,
  DropboxConnectionState,
} from './types';
import {
  getDropboxTokens,
  clearDropboxTokens,
  isDropboxTokenValid,
} from './auth';
import {
  listDropboxFolder,
  listDropboxFolderContinue,
  checkDropboxConnection,
  getDropboxUserInfo,
} from './api';

interface DropboxState {
  // Connection state
  connectionState: DropboxConnectionState;
  connectedEmail: string | null;
  connectedName: string | null;

  // Selected data room folder
  selectedFolder: DropboxFolder | null;

  // Current folder view (for browsing)
  currentPath: string;
  currentFolderPath: DropboxFolder[];

  // Files in current folder
  files: DropboxFile[];
  folders: DropboxFolder[];
  isLoadingFiles: boolean;
  cursor: string | null;
  hasMore: boolean;

  // Error state
  error: string | null;

  // Actions
  setConnectionState: (state: DropboxConnectionState) => void;
  setConnectedUser: (email: string | null, name: string | null) => void;
  setSelectedFolder: (folder: DropboxFolder | null) => void;
  setCurrentPath: (path: string, folderPath: DropboxFolder[]) => void;

  // Async actions
  checkConnection: () => Promise<boolean>;
  loadFilesAndFolders: (path?: string) => Promise<void>;
  loadMoreFiles: () => Promise<void>;
  disconnect: () => Promise<void>;
  reset: () => void;
}

const initialState = {
  connectionState: 'disconnected' as DropboxConnectionState,
  connectedEmail: null as string | null,
  connectedName: null as string | null,
  selectedFolder: null as DropboxFolder | null,
  currentPath: '',
  currentFolderPath: [] as DropboxFolder[],
  files: [] as DropboxFile[],
  folders: [] as DropboxFolder[],
  isLoadingFiles: false,
  cursor: null as string | null,
  hasMore: false,
  error: null as string | null,
};

export const useDropboxStore = create<DropboxState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setConnectionState: (connectionState) => set({ connectionState }),

      setConnectedUser: (connectedEmail, connectedName) =>
        set({ connectedEmail, connectedName }),

      setSelectedFolder: (selectedFolder) => set({ selectedFolder }),

      setCurrentPath: (currentPath, currentFolderPath) =>
        set({ currentPath, currentFolderPath }),

      checkConnection: async () => {
        try {
          const tokens = await getDropboxTokens();
          if (!tokens || !isDropboxTokenValid(tokens)) {
            set({
              connectionState: 'disconnected',
              connectedEmail: null,
              connectedName: null,
            });
            return false;
          }

          const isConnected = await checkDropboxConnection();
          if (isConnected) {
            const userInfo = await getDropboxUserInfo();
            set({
              connectionState: 'connected',
              connectedEmail: userInfo?.email || null,
              connectedName: userInfo?.name || null,
            });
            return true;
          }

          set({
            connectionState: 'disconnected',
            connectedEmail: null,
            connectedName: null,
          });
          return false;
        } catch (error) {
          set({
            connectionState: 'error',
            error:
              error instanceof Error
                ? error.message
                : 'Connection check failed',
          });
          return false;
        }
      },

      loadFilesAndFolders: async (path?: string) => {
        const targetPath = path ?? get().currentPath;
        set({ isLoadingFiles: true, error: null });

        try {
          const result = await listDropboxFolder(targetPath);

          set({
            currentPath: targetPath,
            files: result.files,
            folders: result.folders,
            cursor: result.cursor,
            hasMore: result.hasMore,
            isLoadingFiles: false,
          });
        } catch (error) {
          set({
            isLoadingFiles: false,
            error:
              error instanceof Error ? error.message : 'Failed to load files',
          });
        }
      },

      loadMoreFiles: async () => {
        const { cursor, files, folders } = get();
        if (!cursor) return;

        set({ isLoadingFiles: true });

        try {
          const result = await listDropboxFolderContinue(cursor);

          set({
            files: [...files, ...result.files],
            folders: [...folders, ...result.folders],
            cursor: result.cursor,
            hasMore: result.hasMore,
            isLoadingFiles: false,
          });
        } catch (error) {
          set({
            isLoadingFiles: false,
            error:
              error instanceof Error
                ? error.message
                : 'Failed to load more files',
          });
        }
      },

      disconnect: async () => {
        await clearDropboxTokens();
        set(initialState);
      },

      reset: () => set(initialState),
    }),
    {
      name: 'inverio-dropbox',
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        // Only persist connection info and selected folder
        connectionState: state.connectionState,
        connectedEmail: state.connectedEmail,
        connectedName: state.connectedName,
        selectedFolder: state.selectedFolder,
      }),
    }
  )
);

// Convenience hooks for common selectors
export const useDropboxConnection = () =>
  useDropboxStore(
    useShallow((s) => ({
      connectionState: s.connectionState,
      connectedEmail: s.connectedEmail,
      connectedName: s.connectedName,
      selectedFolder: s.selectedFolder,
    }))
  );

export const useDropboxFiles = () =>
  useDropboxStore(
    useShallow((s) => ({
      files: s.files,
      folders: s.folders,
      isLoading: s.isLoadingFiles,
      hasMore: s.hasMore,
      error: s.error,
    }))
  );
